import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const COLORS = ['#E1A33D', '#5C7E55', '#FBF6EA', '#3B5C4A', '#C2842A', '#B05F3C'];

interface Piece {
  color: string;
  tx: number;
  ty: number;
  rot: number;
  rounded: boolean;
}

interface Burst {
  id: number;
  x: number;
  y: number;
  pieces: Piece[];
}

function makePieces(n: number): Piece[] {
  const out: Piece[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (Math.PI * 2 * i) / n + Math.random() * 0.6;
    const dist = 60 + Math.random() * 70;
    out.push({
      color: COLORS[i % COLORS.length],
      tx: Math.cos(angle) * dist,
      ty: Math.sin(angle) * dist - 26,
      rot: Math.random() * 540 - 270,
      rounded: i % 2 === 1,
    });
  }
  return out;
}

interface ConfettiContextValue {
  fire: (x: number, y: number, count?: number) => void;
}

const ConfettiContext = createContext<ConfettiContextValue>({
  fire: () => {},
});

/**
 * Root-level confetti overlay. Provider mounts once at the app root so all
 * bursts share the same fullscreen canvas — that means `x` and `y` are
 * window-relative coordinates (what you get from Pressable.measureInWindow
 * or a touch event's pageX/pageY), and the centered desktop column doesn't
 * throw the math off.
 *
 * Usage:
 *   const { fire } = useConfetti();
 *   fire(point.x, point.y);
 */
let confettiEnabled = true;
export function setConfettiEnabled(on: boolean) { confettiEnabled = on; }

export function ConfettiProvider({ children }: { children: ReactNode }) {
  const [bursts, setBursts] = useState<Burst[]>([]);

  const fire = useCallback((x: number, y: number, count = 16) => {
    if (!confettiEnabled) return;
    setBursts((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), x, y, pieces: makePieces(count) },
    ]);
  }, []);

  const removeBurst = useCallback((id: number) => {
    setBursts((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const value = useMemo(() => ({ fire }), [fire]);

  return (
    <ConfettiContext.Provider value={value}>
      {children}
      <View pointerEvents="none" style={styles.layer}>
        {bursts.map((b) => (
          <BurstView key={b.id} burst={b} onDone={() => removeBurst(b.id)} />
        ))}
      </View>
    </ConfettiContext.Provider>
  );
}

function BurstView({ burst, onDone }: { burst: Burst; onDone: () => void }) {
  return (
    <>
      {burst.pieces.map((p, i) => (
        <PieceView
          key={i}
          piece={p}
          x={burst.x}
          y={burst.y}
          last={i === burst.pieces.length - 1}
          onLast={onDone}
        />
      ))}
    </>
  );
}

function PieceView({
  piece,
  x,
  y,
  last,
  onLast,
}: {
  piece: Piece;
  x: number;
  y: number;
  last: boolean;
  onLast: () => void;
}) {
  const t = useSharedValue(0);

  useEffect(() => {
    t.value = withTiming(
      1,
      { duration: 720 + Math.random() * 260, easing: Easing.out(Easing.quad) },
      (finished) => {
        if (finished && last) runOnJS(onLast)();
      }
    );
  }, [t, last, onLast]);

  const style = useAnimatedStyle(() => ({
    opacity: 1 - t.value,
    transform: [
      { translateX: t.value * piece.tx },
      { translateY: t.value * piece.ty },
      { rotate: `${t.value * piece.rot}deg` },
    ],
  }));

  return (
    <Animated.View
      style={[
        styles.piece,
        {
          left: x - 4,
          top: y - 4,
          backgroundColor: piece.color,
          borderRadius: piece.rounded ? 9 : 2,
        },
        style,
      ]}
    />
  );
}

export function useConfetti(): ConfettiContextValue {
  return useContext(ConfettiContext);
}

const styles = StyleSheet.create({
  layer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
  },
  piece: {
    position: 'absolute',
    width: 9,
    height: 9,
  },
});
